import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import {
  Table,
  Row,
  Col,
  Select,
  Button,
  Tooltip,
  Popconfirm,
  DatePicker,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import {
  DownloadOutlined,
  FileDoneOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import startCase from 'lodash/startCase';
import moment from 'moment';
import fileDownload from 'js-file-download';

import styles from '../styles';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  getMRR,
  clearMRR,
  getCustomerIdsForMrr,
  getJobchartIdsForMrr,
  getProcessesForMrr,
  getVendorIdsForMrr,
  getRPOIdsForMrr,
  clearCustomerIdsForMrr,
  clearJobchartIdsForMrr,
  clearProcessesForMrr,
  clearVendorIdsForMrr,
  clearRPOIdsForMrr,
} from '../Actions';
import Api from '../Api';
import {
  errorNotification,
  errorNotificationOnBlob,
  successNotification,
  infoNotification,
} from '../Snackbar';

const { Option } = Select;

const MRR = props => {
  const { classes } = props;
  const dispatch = useDispatch();

  const customerIdsForMrr = useSelector(state => state.customerIdsForMrr);
  const jobchartIdsForMrr = useSelector(state => state.jobchartIdsForMrr);
  const processesForMrr = useSelector(state => state.processesForMrr);
  const vendorIdsForMrr = useSelector(state => state.vendorIdsForMrr);
  const rpoIdsForMrr = useSelector(state => state.rpoIdsForMrr);
  const mrr = useSelector(state => state.mrr);

  const [customerId, setCustomerId] = useState(null);
  const [jobchartId, setJobchartId] = useState(null);
  const [process, setProcess] = useState(null);
  const [vendorId, setVendorId] = useState(null);
  const [rpoId, setRpoId] = useState(null);
  const [from, setFrom] = useState(null);
  const [to, setTo] = useState(null);

  const [rows, setRows] = useState([]);

  const onSelectChange = useCallback(
    (selectedRowKeys, selectedRows) => setRows([...selectedRows]),
    []
  );

  useEffect(() => {
    dispatch(getCustomerIdsForMrr());
    dispatch(getJobchartIdsForMrr());
    dispatch(getProcessesForMrr());
    dispatch(getVendorIdsForMrr());
    dispatch(getRPOIdsForMrr());
    return () => {
      dispatch(clearCustomerIdsForMrr());
      dispatch(clearJobchartIdsForMrr());
      dispatch(clearProcessesForMrr());
      dispatch(clearVendorIdsForMrr());
      dispatch(clearRPOIdsForMrr());
      dispatch(clearMRR());
    };
  }, []);

  useEffect(() => {
    dispatch(
      getMRR({ customerId, jobchartId, process, vendorId, rpoId, from, to })
    );
  }, [customerId, jobchartId, process, vendorId, rpoId, from, to]);

  const onProcessed = useCallback(async (id, type) => {
    try {
      let body = {};
      if (type === 'Fabric') {
        body = { fabricId: id };
      } else if (type === 'Accessory') {
        body = { accessoryId: id };
      } else if (type === 'Item') {
        body = { itemId: id };
      } else if (type === 'Style') {
        body = { styleId: id };
      }

      await Api.put(`/store-inwards/mrr/process`, body);
      successNotification('Item processed');

      setCustomerId(null);
      setJobchartId(null);
      setProcess(null);
      setVendorId(null);
      setRpoId(null);
      setFrom(null);
      setTo(null);

      dispatch(
        getMRR({ customerId, jobchartId, process, vendorId, rpoId, from, to })
      );
    } catch (err) {
      errorNotification(err);
    }
  }, []);

  const processedColumn = useMemo(
    () => record =>
      !record.isProcessed ? (
        <Tooltip placement="bottom" title="Processed">
          <Popconfirm
            title="Are you sure job chart is shipped?"
            onConfirm={() => onProcessed(record.id, record.type)}
            okText="Yes"
            cancelText="No"
          >
            <FileDoneOutlined className={classes.tableIcon} />
          </Popconfirm>
        </Tooltip>
      ) : (
        'Processed'
      ),
    []
  );

  const onFileDownload = useCallback(async () => {
    try {
      if (rows.length === 0) {
        infoNotification('Please select at least one item');
        return;
      }
      if (rows.length) {
        const fabricIds = [];
        const accessoryIds = [];
        const itemIds = [];
        const styleIds = [];
        rows.forEach(async row => {
          if (row.type === 'Fabric') {
            fabricIds.push(row.id);
          } else if (row.type === 'Accessory') {
            accessoryIds.push(row.id);
          } else if (row.type === 'Item') {
            itemIds.push(row.id);
          } else if (row.type === 'Style') {
            styleIds.push(row.id);
          }
        });

        const { data } = await Api.postFile('/store-inwards/download/mrr', {
          itemIds,
          fabricIds,
          accessoryIds,
          styleIds,
        });
        fileDownload(data, 'monthly-recurring-report.pdf');
        successNotification('File downloaded');
      }
    } catch (err) {
      errorNotificationOnBlob(err);
    }
  }, [rows]);

  const exportExcelFile = useCallback(async () => {
    try {
      const { data } = await Api.getFile(`/store-inwards/export/mrr`, {
        customerId,
        jobchartId,
        process,
        vendorId,
        rpoId,
        from,
        to,
      });
      fileDownload(
        data,
        `monthly-recurring-report-${moment().format('DD MMM YYYY')}.csv`
      );
    } catch (error) {
      errorNotificationOnBlob(error);
    }
  }, [customerId, jobchartId, process, vendorId, rpoId, from, to]);

  const columns = useMemo(() => {
    const column = [
      {
        title: 'Customer',
        dataIndex: 'customer',
      },
      {
        title: 'BPO #',
        dataIndex: 'bpoNo',
      },
      {
        title: 'Process',
        dataIndex: 'process',
        render: text => startCase(text),
      },
      {
        title: 'Vendor',
        dataIndex: 'vendor',
      },
      {
        title: 'RPO #',
        dataIndex: 'rpoNo',
      },
      {
        title: 'Inward #',
        dataIndex: 'inwardNo',
      },
      {
        title: 'Inward Date',
        dataIndex: 'inwardDate',
        render: inwardDate =>
          inwardDate ? moment(inwardDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Item Type',
        dataIndex: 'type',
      },
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Item',
        dataIndex: 'item',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
      {
        title: 'Accepted',
        dataIndex: 'accepted',
      },
      {
        title: 'Altered',
        dataIndex: 'altered',
      },
      {
        title: 'Rejected',
        dataIndex: 'rejected',
      },
      {
        title: 'Rate',
        dataIndex: 'actualRate',
      },
      {
        title: 'Invoice #',
        dataIndex: 'invoice',
      },
      {
        title: 'Invoice Date',
        dataIndex: 'invoiceDate',
        render: invoiceDate =>
          invoiceDate ? moment(invoiceDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Challan #',
        dataIndex: 'challan',
      },
      {
        title: 'Challan Date',
        dataIndex: 'challanDate',
        render: challanDate =>
          challanDate ? moment(challanDate).format('DD MMM YYYY') : null,
      },
      {
        title: 'Processed',
        render: processedColumn,
        fixed: 'right',
        width: '6%',
      },
    ];

    return column;
  }, []);

  const summaryColumns = useMemo(() => {
    const column = [
      {
        title: 'Process',
        dataIndex: 'process',
        render: text => startCase(text),
      },
      {
        title: 'Type',
        dataIndex: 'type',
      },
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Item',
        dataIndex: 'item',
      },
      {
        title: 'Quantity',
        dataIndex: 'quantity',
      },
    ];

    return column;
  }, []);

  const itemRowKey = useMemo(
    () => record =>
      `B${record.bpoNo}R${record.rpoNo}T${record.type}Id${record.id}`,
    []
  );

  const summaryRowKey = useMemo(
    () => record =>
      `Pr${record.process}T${record.type}It${record.item}Qty${record.quantity}`,
    []
  );

  const scroll = useMemo(() => ({ x: 1700 }), []);

  return (
    <>
      <Breadcrumbs
        moduleName="Monthly Recurring Report"
        moduleLink="/mrr"
        subModuleName=""
      />
      <Row className={classes.paddingToContainerContent}>
        <Col className={classes.marginBetweenSelect}>
          <div>Customer</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select customer"
            optionFilterProp="children"
            onChange={value => {
              setCustomerId(value);
              dispatch(
                getJobchartIdsForMrr({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getProcessesForMrr({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getVendorIdsForMrr({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForMrr({
                  customerId: value,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId,
                })
              );
            }}
          >
            {customerIdsForMrr.map(customer => (
              <Option value={customer.id} key={customer.id}>
                {customer.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>BPO #</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select BPO #"
            optionFilterProp="children"
            onChange={value => {
              setJobchartId(value);
              dispatch(
                getCustomerIdsForMrr({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getProcessesForMrr({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getVendorIdsForMrr({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForMrr({
                  customerId,
                  jobchartId: value,
                  process,
                  vendorId,
                  rpoId,
                })
              );
            }}
          >
            {jobchartIdsForMrr.map(jobchart => (
              <Option value={jobchart.id} key={jobchart.id}>
                {jobchart.bpoNo}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Process</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select Process"
            optionFilterProp="children"
            onChange={value => {
              setProcess(value);
              dispatch(
                getCustomerIdsForMrr({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getJobchartIdsForMrr({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getVendorIdsForMrr({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForMrr({
                  customerId,
                  jobchartId,
                  process: value,
                  vendorId,
                  rpoId,
                })
              );
            }}
          >
            {processesForMrr.map(text => (
              <Option value={text.process} key={text.process}>
                {startCase(text.process)}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Vendor</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select vendor"
            optionFilterProp="children"
            onChange={value => {
              setVendorId(value);
              dispatch(
                getCustomerIdsForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
              dispatch(
                getJobchartIdsForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
              dispatch(
                getProcessesForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
              dispatch(
                getRPOIdsForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId: value,
                  rpoId,
                })
              );
            }}
          >
            {vendorIdsForMrr.map(vendor => (
              <Option value={vendor.id} key={vendor.id}>
                {vendor.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>RPO #</div>
          <Select
            showSearch
            allowClear
            style={{ width: 200 }}
            placeholder="Select RPO #"
            optionFilterProp="children"
            onChange={value => {
              setRpoId(value);
              dispatch(
                getCustomerIdsForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
              dispatch(
                getJobchartIdsForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
              dispatch(
                getProcessesForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
              dispatch(
                getVendorIdsForMrr({
                  customerId,
                  jobchartId,
                  process,
                  vendorId,
                  rpoId: value,
                })
              );
            }}
          >
            {rpoIdsForMrr.map(rpo => (
              <Option value={rpo.id} key={rpo.id}>
                {rpo.rpoNo}
              </Option>
            ))}
          </Select>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>From </div>
          <DatePicker
            placeholder="From Date"
            onChange={value => setFrom(value ? value.toISOString() : null)}
          />
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>To</div>
          <DatePicker
            placeholder="To Date"
            onChange={value => setTo(value ? value.toISOString() : null)}
          />
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Download</div>
          <Button icon={<DownloadOutlined />} onClick={onFileDownload}>
            Download
          </Button>
        </Col>
        <Col className={classes.marginBetweenSelect}>
          <div>Export</div>
          <Button icon={<FileExcelOutlined />} onClick={exportExcelFile}>
            Export
          </Button>
        </Col>
      </Row>
      <br />
      <div className={classes.tableStyle}>
        <h2>Monthly Recurring Report</h2>
        <Table
          bordered
          rowSelection={{
            rows,
            onChange: onSelectChange,
          }}
          size="small"
          columns={columns}
          dataSource={mrr.items}
          rowKey={itemRowKey}
          pagination={false}
          scroll={scroll}
          title={() => 'Items'}
        />
        <br />
        <Table
          bordered
          size="small"
          columns={summaryColumns}
          dataSource={mrr.summary}
          rowKey={summaryRowKey}
          pagination={false}
          scroll={false}
          title={() => 'Items Summary'}
        />
      </div>
    </>
  );
};

MRR.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(MRR);
